import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useAppStore } from '@src/app/context'
import Header from '@src/app/onboarding/Header'
import useTitle from '@src/lib/hooks/useTitle'
import { logError } from '@src/lib/log'
import Button from '@ui/Button/Button'
import { Icon } from '@ui/Icon/Icon'
import Typography from '@ui/Typography'
// eslint-disable-next-line no-restricted-imports -- Deprecated icons should no longer be used -- https://www.notion.so/openphone/Adding-icons-46c2759e72c64013aba963654b55945b#2b999837f9c8459d8d9bd0364eb969d2
import { BrandEmailIcon } from '@ui/icons/DEPRECATED_custom'
import Spacer from '@ui/layout/Spacer'
import { themeClassNames, ThemeProvider } from '@ui/theme'

import type UniversalLoginAppState from './UniversalLoginAppState'
import UniversalLoginLegalFooter from './UniversalLoginLegalFooter'
import * as styles from './UniversalLoginSignup.css'
import signupSplitScreenVisual from './assets/signup-split-screen-visual.webp'

import { markExperimentFromReferral } from '@odyssey/feature-flags/experimentFromReferral'
import { describeCouponDiscount } from '@odyssey/features/billing/utils/describeCouponDiscount'
import useMetaDescription from '@odyssey/hooks/useMetaDescription/useMetaDescription'

const UniversalLoginSignup = () => {
  const { service, electron } = useAppStore()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [referralCode, setReferralCode] = useState<string | null>(null)

  const handleOpenUrl = (url: string) => {
    if (electron) {
      electron.app?.openExternal?.(url).catch(logError)
      return
    }

    window.open(url, '_self')
  }

  useTitle('Sign up and start your free trial')
  useMetaDescription(
    'Create an account to start your Free Quo Trial. Sign up with your email or Google account. Try now for Free!',
  )

  useEffect(() => {
    const inviteCode = searchParams.get('invite')
    const referralCode = searchParams.get('referral_code')
    const phoneNumber = searchParams.get('number')

    if (inviteCode) {
      markExperimentFromReferral()
      service.authV2.fetchAndSetInvite(inviteCode)
    }

    if (referralCode) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- UXP-3732 - Fix React Compiler errors
      setReferralCode(referralCode)
    }

    if (phoneNumber) {
      // eslint-disable-next-line react-hooks/immutability -- UXP-3732 - Fix React Compiler errors
      service.authV2.phoneNumber = phoneNumber
    }
  }, [searchParams, service.authV2])

  useEffect(() => {
    service.analytics.workspace.trackPageView()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: Add missing dependencies or refactor effect (UXF-1509)
  }, [])

  const { title, subtitle } = useMemo(() => {
    const coupon = service.billing.coupon
    const invite = service.authV2.invite

    if (coupon) {
      return {
        title: (
          <>
            Redeem your <br />
            {coupon.name} offer
          </>
        ),
        subtitle: <>Save {describeCouponDiscount(coupon)}</>,
      }
    }

    if (invite) {
      return {
        title: <>Join {invite.name ?? 'your team'} on Quo</>,
        subtitle: <>Create an account to start</>,
      }
    }

    return {
      title: (
        <>
          Welcome to <br />
          Quo!
        </>
      ),
      subtitle: <>Create an account to start your free trial</>,
    }
  }, [service.authV2.invite, service.billing.coupon])

  const getState = (): Omit<UniversalLoginAppState, 'action'> => {
    return {
      inviteToken: service.authV2.inviteToken,
      referralCode: referralCode,
      couponCode: service.billing.coupon?.id ?? null,
      phoneNumber: service.authV2.phoneNumber,
      intent: null,
    }
  }

  const handleGoogleSignup = () => {
    const state = getState()

    service.authV2.auth0Client
      .loginWithRedirect<UniversalLoginAppState>({
        authorizationParams: {
          screen_hint: 'signup',
          prompt: 'select_account',
          connection: 'google-oauth2',
        },
        openUrl: handleOpenUrl,
        appState: { ...state, action: 'google_signup' },
      })
      .catch(logError)
  }

  const handleAppleSignup = () => {
    const state = getState()

    service.authV2.auth0Client
      .loginWithRedirect<UniversalLoginAppState>({
        authorizationParams: {
          screen_hint: 'signup',
          connection: 'apple',
        },
        openUrl: handleOpenUrl,
        appState: { ...state, action: 'apple_signup' },
      })
      .catch(logError)
  }

  const handleEmailSignup = () => {
    const state = getState()

    const email = searchParams.get('email')

    service.authV2.auth0Client
      .loginWithRedirect<UniversalLoginAppState>({
        authorizationParams: {
          screen_hint: 'signup',
          connection: 'email',
          login_hint: email ?? undefined,
        },
        openUrl: handleOpenUrl,
        appState: { ...state, action: 'passwordless_signup' },
      })
      .catch(logError)
  }

  const handleNavigateToLogin = () => {
    navigate('/login')
  }

  const formContent = (
    <div className={styles.card}>
      <Typography
        className={styles.title}
        component="span"
        variant="largeTitle"
        color="textPrimary"
      >
        {title}
      </Typography>
      <Spacer height={8} />
      <Typography variant="body" component="span" color="textSecondary">
        {subtitle}
      </Typography>
      <Spacer height={24} />
      <div className={styles.actionsContainer}>
        <Button
          fullWidth
          onClick={handleGoogleSignup}
          icon={<Icon icon="brand.log-in-with-google--20" />}
          variant="outline"
        >
          <span className={styles.btnContent}>Continue with Google</span>
        </Button>
        <Button
          fullWidth
          onClick={handleAppleSignup}
          icon={<Icon icon="brand.log-in-with-apple--20" />}
          variant="outline"
        >
          <span className={styles.btnContent}>Continue with Apple</span>
        </Button>
        <Button
          fullWidth
          onClick={handleEmailSignup}
          icon={<BrandEmailIcon />}
          variant="outline"
        >
          <span className={styles.btnContent}>Continue with email</span>
        </Button>
      </div>
      <Spacer height={24} />
      <Typography variant="callout" component="span" color="textSecondary">
        Already have an account?
      </Typography>
      <Spacer height={4} />
      <button onClick={handleNavigateToLogin} className={styles.linkButton}>
        Log back in
      </button>
      <Spacer height={96} />
      <UniversalLoginLegalFooter />
    </div>
  )

  return (
    <ThemeProvider themeKey="light">
      <div className={cx(themeClassNames.light, styles.container)}>
        <Header />
        <div className={styles.contentContainer}>
          <div className={styles.background}>{formContent}</div>
          <div className={styles.visualPane}>
            <img
              className={styles.visualImage}
              src={signupSplitScreenVisual}
              alt=""
              data-testid="signup-split-screen-visual"
            />
          </div>
        </div>
      </div>
    </ThemeProvider>
  )
}

export default observer(UniversalLoginSignup)
